import Papa from 'papaparse';
import { TableDataJSON } from '../../src/types/table-json';

/**
 * Parse a CSV file and convert it to TableDataJSON
 * @param file The CSV file to parse
 * @returns A promise that resolves to TableDataJSON
 */
export const parseCSVFile = (file: File): Promise<TableDataJSON> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          // Cast the results.data to the correct type
          const data = results.data as Record<string, string>[];
          const tableData = convertPapaParseToTableDataJSON(data, results.meta.fields || []);
          resolve(tableData);
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(error);
      }
    });
  });
};

/**
 * Convert PapaParse results to TableDataJSON
 * @param data The parsed data from PapaParse
 * @param headers The column headers from PapaParse
 * @returns TableDataJSON instance
 */
export const convertPapaParseToTableDataJSON = (
  data: Record<string, string>[],
  headers: string[]
): TableDataJSON => {
  // Create columns array for TableDataJSON
  const columns = headers.map(header => {
    // Extract values for this column from all rows
    const values = data.map(row => row[header] || '');
    
    // Create a valid ID from the header (replace spaces and special chars)
    const id = header.toLowerCase().replace(/[^a-z0-9]/g, '_');
    
    return {
      id,
      name: header,
      values
    };
  });
  
  return TableDataJSON.from({ columns });
};

/**
 * Handle file selection and parse CSV
 * @param event The file input change event
 * @returns A promise that resolves to TableDataJSON or null if no file selected
 */
export const handleCSVFileSelect = async (
  event: React.ChangeEvent<HTMLInputElement>
): Promise<TableDataJSON | null> => {
  const file = event.target.files?.[0];
  if (!file) return null;
  
  try {
    const tableData = await parseCSVFile(file);
    return tableData;
  } catch (error) {
    console.error('Error parsing CSV file:', error);
    throw error;
  }
}; 